import {
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
  SwitchFieldProps,
  FileFieldProps,
} from '@/components/form-builder/form-builder.types';
import { ContractingAuthorityRepository } from '@/store/contracting-authority-state/contracting-authority-state.repository';
import { of, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ContractingAuthorityService } from '../contracting-authority.service';

interface getContractingAuthorityFormObjectProps {
  store: ContractingAuthorityRepository;
  tuttelOptions: SelectOption[];
  comOptions: SelectOption[];
  paysOptions: SelectOption[];
  isIstTutelDisabled?: boolean;
  commissionTypeOptions?: { name: string; code: string }[];
  getCommissionOptions?: () => { name: string; code: number }[];
  onCommissionTypeChange?: (commissionTypeId: string) => void;
  isAddMode?: boolean;
  isCommissionTypeDisabled?: boolean;
  isDetailsMode?: boolean;
  contractingAuthorityService?: ContractingAuthorityService;
  logoUrl?: string;
}

export const getContractingAuthorityFormObject = ({
  store,
  tuttelOptions,
  paysOptions,
  isIstTutelDisabled = false,
  commissionTypeOptions = [],
  getCommissionOptions = () => [],
  onCommissionTypeChange,
  isAddMode = false,
  isCommissionTypeDisabled = false,
  isDetailsMode = false,
  logoUrl = '',
}: getContractingAuthorityFormObjectProps): DynamicForm => {
  const nameField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.name.label',
    variant: FieldVariant.TEXT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'nom'),
    error: store.getNestedObservable<string>('errors', 'nom'),
    isRequired: true,
    props: {
      value: store.getNestedObservable<string>('dto', 'nom'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, nom: value };
        store.set('dto', dto);
      },
    },
  };

  const abreviationField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.abreviation.label',
    variant: FieldVariant.TEXT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'abreviation'),
    error: store.getNestedObservable<string>('errors', 'abreviation'),
    isRequired: false,
    props: {
      value: store.getNestedObservable<string>('dto', 'abreviation'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, abreviation: value };
        store.set('dto', dto);
      },
    },
  };

  const nomArField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.nomAr.label',
    variant: FieldVariant.TEXT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'nomAr'),
    error: store.getNestedObservable<string>('errors', 'nomAr'),
    isRequired: false,
    props: {
      value: store.getNestedObservable<string>('dto', 'nomAr'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, nomAr: value };
        store.set('dto', dto);
      },
    },
  };

  const tuttelField: DynamicField<SelectFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.tutel.label',
    variant: FieldVariant.SELECT,
    isDisabled: combineLatest([
      store.getNestedObservable<boolean>('isDisabled', 'istTutel'),
      store.getNestedObservable<boolean>('dto', 'istTutel'),
    ]).pipe(map(([isGloballyDisabled, isIstTutel]) => isGloballyDisabled || !!isIstTutel)),
    error: store.getNestedObservable<string>('errors', 'tutelId'),
    isRequired: false,
    props: {
      placeholder: 'settings.contractingAuthority.ac.form.tutel.placeholder',
      options: tuttelOptions,
      filterEnabled: true,
      value: store.getObservable('tuttelSelected').pipe(
        startWith(undefined),
        map(value => value || undefined)
      ),
      onSelectChange: (value: SelectOption) => {
        store.setNested('dto', 'tutelId', value.code);
        store.set('tuttelSelected', value);
      },
    },
  };

  const comField: DynamicField<SelectFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.com.label',
    variant: FieldVariant.SELECT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'comId'),
    error: store.getNestedObservable<string>('errors', 'comId'),
    isRequired: false,
    isHidden: isAddMode, // Hide in add mode
    props: {
      placeholder: 'settings.contractingAuthority.ac.form.com.placeholder',
      options: getCommissionOptions(),
      value: combineLatest([store.getNestedObservable<number>('dto', 'comId'), store.getObservable('comSelected')]).pipe(
        map(([comId, comSelected]) => {
          if (!comId) return undefined;

          // First try to get from comSelected (this should have the full commission data)
          if (comSelected && (comSelected as any).code === comId) {
            return comSelected;
          }

          // If not found in comSelected, try to find in current options
          const currentOptions = getCommissionOptions();
          const found = currentOptions.find(option => option.code === comId);
          if (found) {
            return found;
          }

          // If still not found, return undefined (will be resolved when options are loaded)
          return undefined;
        })
      ),
      onSelectChange: (value: any) => {
        store.setNested('dto', 'comId', value?.code || null);
        store.set('comSelected', value);
      },
    },
  };

  const commissionTypeOptionsWithDisabled = commissionTypeOptions.map(option => ({
    ...option,
    disabled: isAddMode && (option.code === 'TUTELLE' || option.code === 'INTERNE'),
  }));

  const commissionTypeField: DynamicField<SelectFieldProps> = {
    label: 'settings.committee.form.committeeType.label',
    variant: FieldVariant.SELECT,
    isRequired: false,
    isDisabled: isCommissionTypeDisabled ? of(true) : store.getNestedObservable<boolean>('isDisabled', 'typeAffCommission'),
    isHidden: isAddMode, // Hide in add mode
    props: {
      placeholder: 'settings.committee.form.committeeType.placeholder',
      options: commissionTypeOptionsWithDisabled,
      value: store.getNestedObservable<string>('dto', 'typeAffCommission').pipe(
        map(type => {
          if (!type) return undefined;
          return commissionTypeOptionsWithDisabled.find(option => option.code === type);
        })
      ),
      onSelectChange: (value: any) => {
        store.setNested('dto', 'typeAffCommission', value?.code || null);
        // Clear the commission selection when type changes
        store.setNested('dto', 'comId', null);
        store.set('comSelected', undefined);
        // Trigger commission options loading immediately
        if (value?.code && onCommissionTypeChange) {
          onCommissionTypeChange(value.code);
        }
      },
    },
  };

  const emailField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.email.label',
    variant: FieldVariant.TEXT,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'email'),
    error: combineLatest([store.getNestedObservable<string>('errors', 'email'), store.getNestedObservable<string>('dto', 'email')]).pipe(
      map(([serverError, email]) => {
        if (serverError) return serverError;
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Format email invalide';
        return '';
      })
    ),
    isRequired: true,
    props: {
      value: store.getNestedObservable<string>('dto', 'email'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, email: value };
        store.set('dto', dto);
      },
    },
  };

  const logoField: DynamicField<FileFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.logo.label',
    variant: FieldVariant.FILE,
    isRequired: false,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'logo'),
    isHidden: isDetailsMode, // Hide in details mode, show in add and update modes
    props: {
      mode: 'advanced',
      accept: 'image/*',
      multiple: false,
      value: store.getNestedObservable<File>('dto', 'logoFile').pipe(map(file => (file ? [file] : []))),
      onFileSelect: (event: any) => {
        const file = event.files?.[0];
        if (file) {
          const dtoRaw = store.get('dto') || {};
          const dto = { ...dtoRaw, logoFile: file };
          store.set('dto', dto);
        }
      },
      onFileRemoved: (event: any) => {
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, logoFile: undefined };
        store.set('dto', dto);
      },
      files: store.getNestedObservable<string>('dto', 'logo').pipe(
        map(logo => {
          if (!logo) return [];
          const acId = store.getNested<number>('response', 'id');
          if (!acId) return [];

          // For update mode, show existing logo
          if (!isAddMode && logoUrl) {
            // Clean the filename by removing any duplicate extensions
            const cleanFilename = logo.replace(/\.(png|jpg|jpeg|gif)$/i, '').replace(/\.(png|jpg|jpeg|gif)$/i, '');
            const extension = logo.split('.').pop()?.toLowerCase() || 'png';

            return [
              {
                id: logo,
                filename: `${cleanFilename}.${extension}`,
                extension: extension,
                size: undefined, // Remove size to avoid showing "0"
                alfresco_id: logo,
                url: logoUrl,
                preview: logoUrl,
                type: `image/${extension}`,
              },
            ];
          }

          return [];
        })
      ),
      onFileRepresentationChange: (file: any) => {
        // Handle existing logo removal
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, logo: null };
        store.set('dto', dto);
      },
      openFile: (file: any) => {
        if (file.url && file.url !== '' && file.url !== 'about:blank') {
          // For data URLs, we need to ensure they're properly formatted
          if (file.url.startsWith('data:')) {
            window.open(file.url, '_blank');
          } else {
            // For other URLs, try to open them
            window.open(file.url, '_blank');
          }
        }
      },
    },
  };

  const istTutelField: DynamicField<SwitchFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.istTutel.label',
    variant: FieldVariant.SWITCH,
    isDisabled: isIstTutelDisabled ? of(true) : store.getNestedObservable<boolean>('isDisabled', 'istTutel'),
    isRequired: true,
    props: {
      checked: store.getNestedObservable<boolean>('dto', 'istTutel'),
      onChange: (event: any) => {
        const value = typeof event === 'boolean' ? event : event?.checked;
        const dtoRaw = store.get('dto') || {};
        const dto = { ...dtoRaw, istTutel: value };
        store.set('dto', dto);
        if (value) {
          store.set('tuttelSelected', undefined);
          store.setNested('dto', 'tutelId', undefined);
        }
      },
    },
  };

  // Adresse fields
  const adresseFieldsRow1: DynamicField<TextFieldProps>[] = [
    {
      label: 'settings.contractingAuthority.ac.form.adresse.label',
      variant: FieldVariant.TEXT,
      isRequired: false,
      isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
      props: {
        value: store.getNestedObservable<string>('dto', 'adresse', 'adresse'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).adresse || {};
          const dto = { ...dtoRaw, adresse: { ...adresseRaw, adresse: value } };
          store.set('dto', dto);
        },
      },
    },
    {
      label: 'settings.contractingAuthority.ac.form.compAdresse.label',
      variant: FieldVariant.TEXT,
      isRequired: false,
      isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
      props: {
        value: store.getNestedObservable<string>('dto', 'adresse', 'compAdresse'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).adresse || {};
          const dto = { ...dtoRaw, adresse: { ...adresseRaw, compAdresse: value } };
          store.set('dto', dto);
        },
      },
    },
  ];

  const adresseFieldsRow2: DynamicField<TextFieldProps>[] = [
    {
      label: 'settings.contractingAuthority.ac.form.tel1.label',
      variant: FieldVariant.TEL,
      isRequired: false,
      isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
      props: {
        value: store.getNestedObservable<string>('dto', 'adresse', 'tel1'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).adresse || {};
          const dto = { ...dtoRaw, adresse: { ...adresseRaw, tel1: value } };
          store.set('dto', dto);
        },
      },
    },
    {
      label: 'settings.contractingAuthority.ac.form.tel2.label',
      variant: FieldVariant.TEL,
      isRequired: false,
      isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
      props: {
        value: store.getNestedObservable<string>('dto', 'adresse', 'tel2'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).adresse || {};
          const dto = { ...dtoRaw, adresse: { ...adresseRaw, tel2: value } };
          store.set('dto', dto);
        },
      },
    },
    {
      label: 'settings.contractingAuthority.ac.form.fax.label',
      variant: FieldVariant.TEL,
      isRequired: false,
      isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
      props: {
        value: store.getNestedObservable<string>('dto', 'adresse', 'fax'),
        onChange: (value: string) => {
          const dtoRaw = store.get('dto') || {};
          const adresseRaw = (dtoRaw as any).adresse || {};
          const dto = { ...dtoRaw, adresse: { ...adresseRaw, fax: value } };
          store.set('dto', dto);
        },
      },
    },
  ];

  const codePostalField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.codePostal.label',
    variant: FieldVariant.TEL,
    isRequired: false,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
    error: combineLatest([
      store.getNestedObservable<string>('errors', 'adresse', 'codePostal'),
      store.getNestedObservable<string>('dto', 'adresse', 'codePostal'),
    ]).pipe(
      map(([serverError, codePostal]) => {
        if (serverError) return serverError;
        if (codePostal && !/^\d+$/.test(codePostal)) return 'Code postal doit être numérique';
        return '';
      })
    ),
    props: {
      value: store.getNestedObservable<string>('dto', 'adresse', 'codePostal'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const adresseRaw = (dtoRaw as any).adresse || {};
        const dto = { ...dtoRaw, adresse: { ...adresseRaw, codePostal: value } };
        store.set('dto', dto);
      },
    },
  };
  const villeField: DynamicField<TextFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.ville.label',
    variant: FieldVariant.TEXT,
    isRequired: false,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
    props: {
      value: store.getNestedObservable<string>('dto', 'adresse', 'ville'),
      onChange: (value: string) => {
        const dtoRaw = store.get('dto') || {};
        const adresseRaw = (dtoRaw as any).adresse || {};
        const dto = { ...dtoRaw, adresse: { ...adresseRaw, ville: value } };
        store.set('dto', dto);
      },
    },
  };
  const paysField: DynamicField<SelectFieldProps> = {
    label: 'settings.contractingAuthority.ac.form.pays.label',
    variant: FieldVariant.SELECT,
    isRequired: false,
    isDisabled: store.getNestedObservable<boolean>('isDisabled', 'adresse'),
    props: {
      placeholder: 'settings.contractingAuthority.ac.form.pays.placeholder',
      options: paysOptions,
      value: store.getObservable('paysSelected').pipe(
        startWith(undefined),
        map(value => value || undefined)
      ),
      onSelectChange: (value: SelectOption) => {
        const dtoRaw = store.get('dto') || {};
        const adresseRaw = (dtoRaw as any).adresse || {};
        const dto = { ...dtoRaw, adresse: { ...adresseRaw, paysId: value.code } };
        store.set('dto', dto);
        store.set('paysSelected', value);
      },
    },
  };

  const adresseFieldsRow3: DynamicField<TextFieldProps | SelectFieldProps>[] = [codePostalField, villeField];
  if (isDetailsMode) {
    adresseFieldsRow3.push(paysField);
  }

  // const adresseFieldsRow4: DynamicField<TextFieldProps>[] = [
  // {
  //   label: 'settings.contractingAuthority.ac.form.contact.label',
  //   variant: FieldVariant.TEXT,
  //   isRequired: false,
  //   error: store.getNestedObservable<string>('errors', 'contact'),
  //   isDisabled: store.getNestedObservable<boolean>('isDisabled', 'contact'),
  //   props: {
  //     value: store.getNestedObservable<string>('dto', 'contact'),
  //     onChange: (value: string) => {
  //       const dtoRaw = store.get('dto') || {};
  //       const dto = { ...dtoRaw, contact: value };
  //       store.set('dto', dto);
  //     },
  //   },
  // },
  // ];

  return {
    title: 'Contracting Authority',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [nameField, abreviationField] },
          { fields: [nomArField, emailField] },
          { fields: [tuttelField, istTutelField] },
          { fields: [commissionTypeField, comField] }, // Hide commission fields in add mode
          { fields: [logoField] }, // Hide logo field in details mode
          { fields: adresseFieldsRow1 }, // adresse + compAdresse
          { fields: adresseFieldsRow2 }, // tel1 + tel2
          { fields: adresseFieldsRow3 }, // codePostal + ville + pays
        ],
      },
    ],
  };
};
